import DOMPurify from "dompurify";
import { marked } from "marked";

marked.setOptions({ gfm: true, breaks: true });

let linkHosts: string[] | null = null;

/** From the widget config: the hosts answers may link to (null: any). */
export function setLinkHosts(hosts: string[] | null | undefined) {
  linkHosts = hosts?.length ? hosts : null;
}

/** The host itself or a subdomain of it: edach.eu covers surowcechemiczne.edach.eu. */
function allowedLink(href: string): boolean {
  if (!linkHosts || /^(mailto|tel):/i.test(href)) return true;
  try {
    const host = new URL(href).hostname.toLowerCase();
    return linkHosts.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

// Every link is marked so the message component can decide where it goes
// (this site: navigate the page; elsewhere: new tab). A link to a host the
// tenant does not use loses its href and stays as text: a manipulated answer
// cannot hand the visitor a phishing link. Nothing else survives sanitising:
// no images from the model, no styles, no handlers.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A") return;
  if (!allowedLink(node.getAttribute("href") ?? "")) {
    node.removeAttribute("href");
    return;
  }
  node.setAttribute("target", "_blank");
  node.setAttribute("rel", "noopener noreferrer");
});

const ALLOWED_TAGS = ["p", "br", "strong", "em", "b", "i", "ul", "ol", "li", "a", "code", "pre", "blockquote", "h3", "h4", "table", "thead", "tbody", "tr", "th", "td", "hr"];

const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s/;

/**
 * Answers often close a list with a question on the very next line
 * ("- mieszkanie M.34, …\nJaki metraż Cię interesuje?"). Markdown reads that
 * line as the last item's continuation; a person reads a new paragraph, so it
 * gets the blank line the model left out. Indented lines stay in the item.
 */
function closeLists(text: string): string {
  const lines = text.split("\n");
  return lines
    .map((line, i) => (i > 0 && LIST_ITEM.test(lines[i - 1]!) && /^\S/.test(line) && !LIST_ITEM.test(line) ? `\n${line}` : line))
    .join("\n");
}

export function renderMarkdown(text: string): string {
  const html = marked.parse(closeLists(text), { async: false }) as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
  });
}
